import { SessionState } from 'common/store/session/reducer';
import { getUrlParam } from 'common/util/url/urlUtil';
import { HitData } from 'common/store/common/search/types';
import commonNavigationService, { AppSpecifiedNavigationListService } from 'common/services/commonNavigationService';
import { tryInterceptUnsavedMarkingsNavigation } from 'common/features/footer/unsavedMarkingsGuard';
import { workspaceID } from 'common/store/common/constants';
import allPartiesJson from '../../docs/definitions/tabs/allParties.json';
import appealsAllCandidates from '../../docs/definitions/tabs/appealsAllCandidates.json';
import explanationRequested from '../../docs/definitions/tabs/explanationRequested.json';
import oneCandidate from '../../docs/definitions/tabs/oneCandidate.json';
import explanationNotRequested from '../../docs/definitions/tabs/explanationNotRequested.json';
import flaggedPartiesJson from '../../docs/definitions/tabs/flaggedParties.json';
import toMarkPartiesJson from '../../docs/definitions/tabs/toMarkParties.json';
import notGradedPartiesJson from '../../docs/definitions/tabs/notGradedParties.json';
import allQuestionsJson from '../../docs/definitions/tabs/allQuestions.json';
import manualQuestionsJson from '../../docs/definitions/tabs/manualQuestions.json';
import manualQuestionsDoneJson from '../../docs/definitions/tabs/manualQuestionsDone.json';
import manualAssessmentRunQuestionsJson from '../../docs/definitions/tabs/manualAssessmentRunQuestions.json';
import automaticQuestionsJson from '../../docs/definitions/tabs/automaticQuestions.json';
import workflowNotStarted from '../../docs/definitions/tabs/workflowNotStarted.json';
import workflowInProgress from '../../docs/definitions/tabs/workflowInProgress.json';
import workflowConfirmed from '../../docs/definitions/tabs/workflowConfirmed.json';
import appealsNotStarted from '../../docs/definitions/tabs/appealsNotStarted.json';
import appealsInProgress from '../../docs/definitions/tabs/appealsInProgress.json';
import appealsConfirmed from '../../docs/definitions/tabs/appealsConfirmed.json';
import explanationStatusEmpty from '../../docs/definitions/tabs/explanationStatusEmpty.json';
import explanationStatusDraft from '../../docs/definitions/tabs/explanationStatusDraft.json';
import explanationStatusRead from '../../docs/definitions/tabs/explanationStatusRead.json';
import explanationStatusReady from '../../docs/definitions/tabs/explanationStatusReady.json';
import explanationStatusSent from '../../docs/definitions/tabs/explanationStatusSent.json';
import explanationRequestedInComplete from '../../docs/definitions/tabs/explanationRequestedInComplete.json';
import gradeNotSet from '../../docs/definitions/tabs/gradeNotSet.json';
import conflictingEvaluatorGradesJson from '../../docs/definitions/tabs/conflictingEvaluatorGrades.json';
import allAssessmentRunQuestionsJson from '../../docs/definitions/tabs/allAssessmentRunQuestions.json';
import allAssessmentRunsJson from '../../docs/definitions/tabs/allAssessmentRuns.json';
import assessmentPathChildrenAssessmentRunsJson from '../../docs/definitions/tabs/assessmentPathChildrenAssessmentRuns.json';
import allGradingReadyIssuesJson from '../../docs/definitions/tabs/allGradingReadyIssues.json';
import manuallyGraded from '../../docs/definitions/tabs/manuallyGraded.json';
import alreadyConfirmed from '../../docs/definitions/tabs/alreadyConfirmed.json';
import markingNotCompleted from '../../docs/definitions/tabs/markingNotCompleted.json';
import partiesWithPendingStatus from '../../docs/definitions/tabs/partiesWithPendingStatus.json';
import answeredQuestionsJson from '../../docs/definitions/tabs/answeredQuestions.json';
import validationIssueQuestionsJson from '../../docs/definitions/tabs/validationIssueQuestions.json';
import notGradedCandidatesJson from '../../docs/definitions/tabs/notGraded.json';
import flaggedCandidatesJson from '../../docs/definitions/tabs/flaggedCandidates.json';
import notMarkedQuestions from '../../docs/definitions/tabs/notMarkedQuestions.json';
import unmarkedQuestions from '../../docs/definitions/tabs/unmarkedQuestions.json';
import answeredCandidatesJson from '../../docs/definitions/tabs/answeredCandidates.json';
import incompleteMarkingQuestionsJson from '../../docs/definitions/tabs/incompleteMarkingQuestions.json';

import { ContextState } from '../store/context/reducer';
import { RootState } from '../store/rootReducer';
import store from '../store';
import {
    ASSESSMENTRUNQUESTION_INDEX,
    CANDIDATE_INDEX,
    GRADING_PARTY_INDEX,
    USERQUESTION_INDEX,
    GRADINGREADY_ISSUE, ASSESSMENTRUN_INDEX,
} from '../store/common/search/constants';
import urlContextParametersService from './urlContextParametersService';
import { openAiSupportedMarkingIntro } from '../features/aiSupportedMarkingIntro/actions';
import { shouldOpenAiIntro } from '../features/aiSupportedMarkingIntro/guard';
import { isAiIntroKnownReadOnly } from '../features/aiSupportedMarkingIntro/introReadOnly';
import { getLoggedInUserId } from '../features/aiSupportedMarkingIntro/dontShowAgainPreference';

const getSession = (): SessionState => (store.getState() as RootState).session;

const navigationListService: AppSpecifiedNavigationListService = {
    getDefaultTabsForDataType(dataType: string): any[] {
        switch (dataType) {
        case GRADING_PARTY_INDEX: {
            const gradingPartyTabs = [allPartiesJson, notGradedCandidatesJson, flaggedCandidatesJson,
                flaggedPartiesJson, toMarkPartiesJson, conflictingEvaluatorGradesJson,
                notGradedPartiesJson, manuallyGraded, alreadyConfirmed,
                workflowNotStarted, workflowInProgress, workflowConfirmed, gradeNotSet, markingNotCompleted,
                explanationRequested, explanationNotRequested, explanationStatusEmpty, explanationStatusDraft,
                explanationStatusReady, explanationStatusSent, explanationStatusRead, explanationRequestedInComplete,
                appealsAllCandidates, appealsNotStarted, appealsInProgress, appealsConfirmed, oneCandidate, partiesWithPendingStatus];
            if ((window as any).marketplaceProperties?.ENABLE_FILTER_INCOMPLETE_MARKED_QUESTIONS) {
                gradingPartyTabs.push(answeredCandidatesJson);
                if (getUrlParam('workspaceId') === workspaceID.PLAN_MARKING || getUrlParam('workspaceId') === workspaceID.PLAN_OVERVIEW) {
                    return gradingPartyTabs.filter((tab: any) => tab.id !== 'toMarkParties');
                }
            }
            return gradingPartyTabs;
        }
        case USERQUESTION_INDEX: {
            const userQuestionTabs = [allQuestionsJson, manualQuestionsJson, automaticQuestionsJson, manualQuestionsDoneJson,
                answeredQuestionsJson, validationIssueQuestionsJson, notMarkedQuestions];
            if ((window as any).marketplaceProperties?.ENABLE_FILTER_INCOMPLETE_MARKED_QUESTIONS) {
                userQuestionTabs.push(unmarkedQuestions);
            }
            return userQuestionTabs;
        }
        case ASSESSMENTRUNQUESTION_INDEX:
            return [allAssessmentRunQuestionsJson, manualAssessmentRunQuestionsJson, incompleteMarkingQuestionsJson];
        case ASSESSMENTRUN_INDEX:
            return [allAssessmentRunsJson, assessmentPathChildrenAssessmentRunsJson];
        case GRADINGREADY_ISSUE:
            return [allGradingReadyIssuesJson];
        default:
            return [];
        }
    },
    getContextValue(context: ContextState, contextKey: string): any {
        const rawIndex: number = contextKey.indexOf('.raw');
        const key: string = rawIndex > -1 ? contextKey.substring(0, rawIndex) : contextKey;
        switch (key) {
        case 'candidateId':
        case 'partyName':
            return context.partyState.partyName;
        case 'questionNumber':
            return context.questionState.questionNumber;
        case 'questionNumbering':
            return context.questionState.questionNumbering;
        default:
            return context[contextKey];
        }
    },

    getEmptyHitResponse(context: ContextState, dataType: string,): HitData[] {
        const { partyState, questionState } = context;
        switch (dataType) {
        case CANDIDATE_INDEX:
            return [
                {
                    candidateId: partyState.partyName,
                    userId: context.partyId,
                },
            ];
        case GRADING_PARTY_INDEX:
            return [
                {
                    partyName: partyState.partyName,
                    partyId: context.partyId,
                },
            ];
        case USERQUESTION_INDEX:
            return [
                {
                    questionId: context.questionId,
                    questionNumbering: questionState.questionNumbering,
                    questionNumber: questionState.questionNumber,
                    questionType: questionState.questionType,
                    documentContentRevisionId: questionState.documentContentRevisionId,
                },
            ];
        default:
            return [];
        }
    },
    getHitValuesFromNavigation(hit: HitData, dataType: string,) {
        switch (dataType) {
        case CANDIDATE_INDEX:
            return {
                userId: hit.userId,
            };
        case GRADING_PARTY_INDEX:
            return {
                partyId: hit.partyId,
            };
        case USERQUESTION_INDEX:
            return {
                questionId: hit.questionId,
            };
        default:
            return {};
        }
    },

    navigateToWorkspace(workspaceId: string, silent?: boolean): void {
        const state = store.getState() as RootState;
        if ((workspaceId === workspaceID.PLAN_MARKING || workspaceId === workspaceID.EVALUATE_MARKING)
            && shouldOpenAiIntro(state, workspaceId)
            && !isAiIntroKnownReadOnly((state as any).context?.partyState?.gradingPartyDocument)) {
            store.dispatch(openAiSupportedMarkingIntro(
                workspaceId,
                getLoggedInUserId(),
                (state as any).context?.assessmentRunId,
            ));
        }
        commonNavigationService.setSetParamsInSearchString({ workspaceId }, { silent });
    },

    navigateTo(hit: HitData, dataType: string): any {
        const proceed = () => {
            switch (dataType) {
            case CANDIDATE_INDEX:
                return urlContextParametersService.setPartyId(hit.userId);
            case GRADING_PARTY_INDEX:
                return urlContextParametersService.setPartyId(hit.partyId);
            case USERQUESTION_INDEX:
                return urlContextParametersService.setQuestionId(hit.questionId);
            default:
                return undefined;
            }
        };
        if (tryInterceptUnsavedMarkingsNavigation(proceed)) {
            return undefined;
        }
        return proceed();
    },

    backToOriginalTool(context: ContextState): void {
        const session = getSession();
        const isStudent = session.user.isStudent || false;
        const isInPreview = getUrlParam('previewScheme');
        if (!isInPreview) {
            if (isStudent) {
                location.href = `/student#list/${context.assessmentRunId}`;
            } else {
                location.href = `/admin#grading/overview/${context.assessmentRunId}`;
            }
        }
    },

    backToOriginalLists(context: ContextState): void {
        const session = getSession();
        const isStudent = session.user.isStudent || false;
        const isInPreview = getUrlParam('previewScheme');
        if (!isInPreview) {
            if (isStudent) {
                location.href = `/student#list/${context.assessmentRunId}`;
            } else {
                location.href = '/admin#grading/new';
            }
        }
    },
    redirectToLogin(): void {
        const session = getSession();
        const isStudent = session.user.isStudent || false;
        const redirectLocation = isStudent ? 'student' : 'admin';
        location.href = `/${redirectLocation}?alert=noSession`;
    },
    validateContext: () => {
        return false;
    },
};

export default navigationListService;
